import { makeObservable, observable, runInAction, computed } from "mobx";
import {
  Model,
  getOrganizerVotings,
  getParticipations,
  getMyGroups,
  getPublicVotings,
  getProfile,
  ApiVotingListItem,
  ParticipationItem,
  MyVoterGroup,
  VOTING_STATUSES,
  DATE_SORTERS
} from "@lubbezposrednio-frontend/core";

export type ActionKind = "vote" | "results" | "draft" | "signup" | "confirm_group";
export type ActionItem = { kind: ActionKind; title: string; to: string };
export type ActivityKind = "started" | "results" | "anchored";
export type ActivityEvent = { kind: ActivityKind; title: string; id: string; at: number };

const RECENT_MS = 21 * 24 * 60 * 60 * 1000; // 3 tygodnie

export class OrganizerDashboardModel extends Model {
  votings: ApiVotingListItem[] = [];
  participations: ParticipationItem[] = [];
  discoverVotings: ApiVotingListItem[] = [];
  myGroups: MyVoterGroup[] = [];
  userName = "";
  avatarUrl = "";
  verified = false;
  profileRef = "";
  isOrgAccount = false;
  isLoading = true;

  constructor() {
    super();

    makeObservable(this, {
      votings: observable,
      participations: observable,
      discoverVotings: observable,
      myGroups: observable,
      userName: observable,
      avatarUrl: observable,
      verified: observable,
      profileRef: observable,
      isOrgAccount: observable,
      isLoading: observable,
      recent: computed,
      upcomingParticipations: computed,
      discoverTop: computed,
      myGroupsCount: computed,
      isOrganizer: computed,
      toVoteParticipations: computed,
      resultsReadyParticipations: computed,
      ongoingParticipationsCount: computed,
      actionItems: computed,
      activityFeed: computed,
      totalVoters: computed,
      activeVotersCount: computed,
      totalVotes: computed,
      openSignupsCount: computed,
      draftCount: computed,
      plannedCount: computed,
      finishedCount: computed,
      ongoingCount: computed,
      anchoredCount: computed,
      totalMembers: computed,
      nextDeadline: computed,
      openSignups: computed,
      bestTurnout: computed,
      averageTurnout: computed
    });
  }

  override async onInit() {
    try {
      // Każdy request łapie błąd osobno — jeden 401/timeout nie może wygasić CAŁEGO pulpitu.
      const [votingsRes, participationsRes, groupsRes, discoverRes, profileRes] = await Promise.all([
        getOrganizerVotings({ per_page: 50, sort: DATE_SORTERS.DATE_DESC }).catch(() => null),
        getParticipations().catch(() => null),
        getMyGroups().catch(() => null),
        getPublicVotings({ per_page: 12, status: [VOTING_STATUSES.ONGOING, VOTING_STATUSES.PLANNED] }).catch(() => null),
        getProfile().catch(() => null)
      ]);

      runInAction(() => {
        this.votings = (votingsRes?.data?.votings as ApiVotingListItem[]) ?? [];
        this.participations = participationsRes?.data?.votings ?? [];
        this.myGroups = groupsRes?.data?.voter_groups ?? [];
        this.discoverVotings = (discoverRes?.data?.votings as ApiVotingListItem[]) ?? [];
        const u = profileRes?.data?.user;
        this.userName = u?.is_organization ? u?.organization_name || "" : u?.first_name || "";
        this.avatarUrl = u?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${u.avatar_url}` : "";
        this.verified = !!u?.verified;
        this.profileRef = u?.profile_public && u?.profile_ref ? u.profile_ref : "";
        this.isOrgAccount = !!u?.is_organization;
      });
    } catch (error) {
      console.error("[OrganizerDashboardModel]", error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  countBy(status: VOTING_STATUSES) {
    return this.votings.filter(v => v.status === status).length;
  }

  get isOrganizer() {
    return this.votings.length > 0;
  }

  get myGroupsCount() {
    return this.myGroups.length;
  }

  // ── Centrum akcji: derywacje ──
  get toVoteParticipations() {
    return this.participations.filter(p => p.status === VOTING_STATUSES.ONGOING && !p.voted);
  }

  get resultsReadyParticipations() {
    const now = Date.now();
    return this.participations.filter(p => p.status === VOTING_STATUSES.FINISHED && now - new Date(p.end_date).getTime() < RECENT_MS);
  }

  get ongoingParticipationsCount() {
    return this.participations.filter(p => p.status === VOTING_STATUSES.ONGOING).length;
  }

  get actionItems(): ActionItem[] {
    const items: ActionItem[] = [];
    this.toVoteParticipations.forEach(p => items.push({ kind: "vote", title: p.title, to: `/glosowanie/${p.id}` }));
    this.resultsReadyParticipations.forEach(p => items.push({ kind: "results", title: p.title, to: `/glosowanie/${p.id}/zakonczone` }));
    this.votings
      .filter(v => v.status === VOTING_STATUSES.DRAFT)
      .forEach(v => items.push({ kind: "draft", title: v.title || "Szkic bez tytułu", to: "/organizator/twoje-glosowania" }));
    this.votings
      .filter(v => v.electorate_type === "signup" && v.signup_open)
      .forEach(v => items.push({ kind: "signup", title: v.title, to: `/organizator/glosowanie/${v.id}` }));
    this.myGroups
      .filter(g => g.membership_status === "pending")
      .forEach(g => items.push({ kind: "confirm_group", title: g.name, to: "/organizator/moje-grupy" }));
    return items;
  }

  // ── Feed aktywności: syntetyzowany z dat (ruszyło / wyniki / zakotwiczono) ──
  get activityFeed(): ActivityEvent[] {
    const now = Date.now();
    const seen = new Set<string>();
    const list = [...this.participations, ...this.votings].filter(v => {
      if (seen.has(v.id)) return false;
      seen.add(v.id);
      return true;
    });

    const events: ActivityEvent[] = [];
    list.forEach(v => {
      const start = v.start_date ? new Date(v.start_date).getTime() : 0;
      const end = v.end_date ? new Date(v.end_date).getTime() : 0;
      if (start && start <= now && now - start < RECENT_MS) {
        events.push({ kind: "started", title: v.title, id: v.id, at: start });
      }
      if (end && end <= now && now - end < RECENT_MS) {
        events.push({ kind: "results", title: v.title, id: v.id, at: end });
        events.push({ kind: "anchored", title: v.title, id: v.id, at: end + 1000 });
      }
    });
    return events.sort((a, b) => b.at - a.at).slice(0, 8);
  }

  get totalVoters() {
    return this.votings.reduce((sum, v) => sum + (v.voters_count ?? 0), 0);
  }

  get activeVotersCount() {
    return this.votings
      .filter(v => v.status === VOTING_STATUSES.ONGOING || v.status === VOTING_STATUSES.PLANNED)
      .reduce((sum, v) => sum + (v.voters_count ?? 0), 0);
  }

  get totalVotes() {
    return this.votings.reduce((sum, v) => sum + (v.votes_count ?? 0), 0);
  }

  get openSignupsCount() {
    return this.votings.filter(v => v.electorate_type === "signup" && v.signup_open).length;
  }

  // ── Rozszerzone metryki ──
  get draftCount() {
    return this.countBy(VOTING_STATUSES.DRAFT);
  }

  get plannedCount() {
    return this.countBy(VOTING_STATUSES.PLANNED);
  }

  get finishedCount() {
    return this.countBy(VOTING_STATUSES.FINISHED);
  }

  get ongoingCount() {
    return this.countBy(VOTING_STATUSES.ONGOING);
  }

  // Zakończone głosowania mają zakotwiczony protokół na blockchainie.
  get anchoredCount() {
    return this.finishedCount;
  }

  get totalMembers() {
    return this.myGroups.reduce((sum, g) => sum + (g.members_count ?? 0), 0);
  }

  get averageTurnout() {
    return this.totalVoters > 0 ? Math.round((this.totalVotes / this.totalVoters) * 100) : 0;
  }

  // Najbliżej kończące się trwające głosowanie (do odliczania na pulpicie).
  get nextDeadline(): { id: string; title: string; end: number } | null {
    const now = Date.now();
    const upcoming = this.votings
      .filter(v => v.status === VOTING_STATUSES.ONGOING && v.end_date)
      .map(v => ({ id: v.id, title: v.title, end: new Date(v.end_date).getTime() }))
      .filter(v => v.end > now)
      .sort((a, b) => a.end - b.end);
    return upcoming[0] ?? null;
  }

  // Głosowania z otwartymi zapisami (z datą zamknięcia, jeśli jest).
  get openSignups() {
    return this.votings
      .filter(v => v.electorate_type === "signup" && v.signup_open)
      .map(v => ({ id: v.id, title: v.title, closesAt: v.signup_closes_at ?? null }))
      .slice(0, 4);
  }

  // Zakończone głosowanie z najwyższą frekwencją.
  get bestTurnout(): { id: string; title: string; pct: number } | null {
    const finished = this.votings
      .filter(v => v.status === VOTING_STATUSES.FINISHED && (v.voters_count ?? 0) > 0)
      .map(v => ({ id: v.id, title: v.title, pct: Math.round(((v.votes_count ?? 0) / (v.voters_count ?? 1)) * 100) }))
      .sort((a, b) => b.pct - a.pct);
    return finished[0] ?? null;
  }

  get recent() {
    return this.votings.slice(0, 6);
  }

  get upcomingParticipations() {
    return this.participations.filter(p => p.status === VOTING_STATUSES.ONGOING || p.status === VOTING_STATUSES.PLANNED).slice(0, 4);
  }

  get discoverTop() {
    const score = (v: ApiVotingListItem) =>
      v.status === VOTING_STATUSES.ONGOING ? 0 : (v as { signup_open?: boolean }).signup_open ? 1 : 2;
    return [...this.discoverVotings].sort((a, b) => score(a) - score(b)).slice(0, 3);
  }
}
